import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { TYPE_MAP, NZ_CENTER } from "@/lib/mapConfig";

export const relatedPosts = (m, posts) =>
  posts.filter((p) => (m.linked_post_ids || []).includes(p.id) || (p.linked_marker_ids || []).includes(m.id));

const centreOf = (m) => (m.geometry === "line" && m.coordinates?.length ? m.coordinates[Math.floor(m.coordinates.length / 2)] : [m.lat, m.lng]);

export const parseMW = (capacity) => {
  const match = /(\d+(?:\.\d+)?)\s*(g|m)?w/i.exec(capacity || "");
  if (!match) return null;
  const n = parseFloat(match[1]);
  return match[2]?.toLowerCase() === "g" ? n * 1000 : n;
};

export const radiusFor = (m) => {
  const mw = parseMW(m.capacity);
  if (mw == null) return 8;
  return Math.max(7, Math.min(22, 5 + Math.sqrt(mw) * 1.6));
};

function FocusOn({ marker, refs }) {
  const map = useMap();
  useEffect(() => {
    if (!marker) return;
    const c = centreOf(marker);
    if (c[0] == null) return;
    map.flyTo(c, marker.geometry === "line" ? 7 : 11, { duration: 1.2 });
    const t = setTimeout(() => refs.current[marker.id]?.openPopup(), 1300);
    return () => clearTimeout(t);
  }, [marker, map, refs]);
  return null;
}

export default function MapView({ markers, posts = [], height = "600px", zoom = 6, interactive = true, focusId = null }) {
  const refs = useRef({});
  const focusMarker = markers.find((m) => m.id === focusId);

  return (
    <MapContainer
      center={NZ_CENTER}
      zoom={zoom}
      scrollWheelZoom={interactive}
      dragging={interactive}
      style={{ height, width: "100%", borderRadius: "8px", zIndex: 1 }}
      attributionControl={true}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; OpenStreetMap contributors'
      />
      <FocusOn marker={focusMarker} refs={refs} />
      {markers.map((m) => {
        const t = TYPE_MAP[m.type] || { color: "#0B0F17", name: m.type };
        const setRef = (el) => { refs.current[m.id] = el; };
        if (m.geometry === "line" && m.coordinates?.length > 1) {
          return (
            <Polyline key={m.id} ref={setRef} positions={m.coordinates} pathOptions={{ color: t.color, weight: 3, opacity: 0.85, dashArray: m.type === "underwater_cables" ? "6 6" : null }}>
              <Tooltip sticky>{m.name}</Tooltip>
              <Popup>
                <MarkerPopup m={m} t={t} posts={posts} />
              </Popup>
            </Polyline>
          );
        }
        if (m.lat == null || m.lng == null) return null;
        return (
          <CircleMarker key={m.id} ref={setRef} center={[m.lat, m.lng]} radius={radiusFor(m)}
            pathOptions={{ color: "#fff", weight: 2, fillColor: t.color, fillOpacity: 0.9 }}>
            <Tooltip>{m.name}</Tooltip>
            <Popup>
              <MarkerPopup m={m} t={t} posts={posts} />
            </Popup>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}

function MarkerPopup({ m, t, posts }) {
  const linked = relatedPosts(m, posts);
  return (
    <div style={{ minWidth: 230, maxWidth: 280 }} data-testid={`marker-popup-${m.id}`}>
      <div style={{ fontFamily: "Roboto Mono, monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: t.color, fontWeight: 600 }}>
        {t.name}
      </div>
      <div style={{ fontFamily: "Roboto, sans-serif", fontSize: 17, fontWeight: 600, marginTop: 4, color: "#11181C" }}>{m.name}</div>
      {m.operator && <div style={{ fontSize: 12, color: "#52606D", marginTop: 2 }}>{m.operator}{m.capacity ? ` · ${m.capacity}` : ""}</div>}
      {m.status_label && <div style={{ fontSize: 11, color: "#52606D", marginTop: 2 }}>{m.status_label}{m.region ? ` · ${m.region}` : ""}</div>}
      {m.description && <p style={{ fontSize: 12.5, color: "#52606D", marginTop: 6, lineHeight: 1.5 }}>{m.description}</p>}
      {linked.length > 0 && (
        <div style={{ marginTop: 10, borderTop: "1px solid #E2E8F0", paddingTop: 8 }} data-testid={`marker-popup-articles-${m.id}`}>
          <div style={{ fontFamily: "Roboto Mono, monospace", fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.12em", color: "#52606D", marginBottom: 6 }}>
            Related research ({linked.length})
          </div>
          {linked.map((p) => (
            <Link key={p.id} to={`/research/${p.slug}`} style={{ display: "block", fontSize: 12.5, color: "#059669", fontWeight: 600, textDecoration: "none", marginBottom: 5, lineHeight: 1.35 }} data-testid={`marker-popup-article-${p.id}`}>
              → {p.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
